import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Invoice/statement status — mirrors the F23 `CommissionStatementStatus` family
 * plus the canvas `overdue` display state. Kept local — `@bravemove/types` is NOT a
 * dep of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/revenue-lib.jsx`](../../../../../docs/design/screens/revenue-lib.jsx)
 * §3 `InvoiceStatusBadge` (line 503).
 */
export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'refunded';
export type InvoiceStatusBadgeSize = 'sm' | 'md';

export interface InvoiceStatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly status: InvoiceStatus;
  readonly size?: InvoiceStatusBadgeSize;
  /** Override the default status label. */
  readonly label?: string;
}

interface InvoiceStatusStyle {
  readonly box: string;
  readonly dot: string;
  readonly defaultLabel: string;
  /** `overdue` reads as urgent via a dashed border per the canvas spec. */
  readonly dashed: boolean;
}

const STATUS_STYLE: Record<InvoiceStatus, InvoiceStatusStyle> = {
  draft: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Draft',
    dashed: false,
  },
  issued: {
    box: 'bg-warning/15 text-ink border border-warning/40',
    dot: 'bg-warning',
    defaultLabel: 'Issued',
    dashed: false,
  },
  paid: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Paid',
    dashed: false,
  },
  overdue: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Overdue',
    dashed: true,
  },
  refunded: {
    box: 'bg-info/15 text-ink border border-info/40',
    dot: 'bg-info',
    defaultLabel: 'Refunded',
    dashed: false,
  },
};

const SIZE: Record<InvoiceStatusBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F23 / F38 — `InvoiceStatusBadge` (shared primitive). Statement / invoice status
 * pill across the venue commission view, the admin statement queue, and the
 * subscription invoices list. `overdue` carries a dashed border for urgency.
 *
 * @example
 *   <InvoiceStatusBadge status="paid" />
 *   <InvoiceStatusBadge status="overdue" size="sm" />
 */
export function InvoiceStatusBadge({
  status,
  size = 'md',
  label,
  className,
  ...rest
}: InvoiceStatusBadgeProps) {
  const style = STATUS_STYLE[status];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Invoice status: ${text}`}
      data-testid={`invoice-status-badge-${status}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-extrabold tracking-[0.1em]',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn('inline-block rounded-full shrink-0', sz.dot, style.dot)}
      />
      <span>{text}</span>
    </span>
  );
}
